import React from 'react';
import { Box, Tooltip, Skeleton, Button, createStyles } from '@mantine/core';
import { IconFlask, IconSettings, IconArrowRight } from '@tabler/icons-react';
import { formatMoney, formatCount, formatDeltaText } from '../formatters';
import type { BusinessDashboardData } from '../dashboardTypes';

const REVENUE_EMPTY_TOOLTIP = 'Available when revenue data is connected';
const SPEND_EMPTY_TOOLTIP = 'Spend data not available';

// Spec colours for the 3-threshold cost benchmark status (Card 3 + tables).
// Order: at/below target → green, within configured % above → amber, beyond → red.
const BENCHMARK_COLOR: Record<string, string> = {
  green: 'rgb(34, 197, 94)',
  amber: 'rgb(245, 158, 11)',
  red: 'rgb(239, 68, 68)',
};
function benchmarkColor(status?: string): string | undefined {
  if (!status || status === 'no_benchmark') return undefined;
  return BENCHMARK_COLOR[status];
}

interface BusinessDashboardViewProps {
  data: BusinessDashboardData;
  costThreshold: number;
  dateRangeLabel: string;
  onNavigateToReports: () => void;
  onNavigateToTests: () => void;
  /** Dashboard is currently fetching data — render skeleton scaffold. */
  loading?: boolean;
  /** Whether the selected site has any campaigns at all. */
  siteHasCampaigns?: boolean;
  /** Number of active tests on the selected site. */
  activeTestsCount?: number;
  /** Open the dashboard settings panel (for the "Select campaigns" empty state). */
  onOpenSettings?: () => void;
}

const useStyles = createStyles((theme) => {
  const dk = theme.colorScheme === 'dark';
  return {
    body: { padding: '24px 28px' },

    /* ── 5 KPI Cards ── */
    kpiGrid: {
      display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 14, marginBottom: 18,
      [`@media (max-width:${theme.breakpoints.md}px)`]: { gridTemplateColumns: 'repeat(3,1fr)' },
      [`@media (max-width:${theme.breakpoints.xs}px)`]: { gridTemplateColumns: '1fr' },
    },
    slot: {
      border: `1px solid ${dk ? '#333' : '#E8E8E8'}`, borderRadius: 10,
      padding: '18px 20px', background: dk ? '#1e1e1e' : '#FAFAFA',
    },
    slotLabel: { fontSize: 11, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase' as const, color: dk ? '#909296' : '#999', margin: 0 },
    slotGoal: { fontSize: 12, fontWeight: 500, color: dk ? '#aaa' : '#555', margin: '2px 0 6px' },
    slotVal: { fontSize: 30, fontWeight: 700, lineHeight: 1, letterSpacing: '-.02em', color: dk ? '#e0e0e0' : '#1A1A1A', margin: 0 },
    slotValAmber: { color: 'rgb(245, 158, 11)' },
    slotValRed: { color: 'rgb(239, 68, 68)' },
    slotSub: { fontSize: 12, color: dk ? '#909296' : '#999', marginTop: 6 },
    slotBench: { fontSize: 12, color: 'rgb(245, 158, 11)', fontWeight: 500, marginTop: 4 },

    /* ── Profitability summary line ── */
    summaryDot: { width: 10, height: 10, borderRadius: '50%', display: 'inline-block', marginRight: 8, verticalAlign: 'middle' },
    summaryLine: { fontSize: 13, color: dk ? '#bbb' : '#555', marginBottom: 6 },

    /* ── Top Contributors ── */
    contribWrap: {
      border: `1px solid ${dk ? '#333' : '#E8E8E8'}`, borderRadius: 10,
      background: dk ? '#1e1e1e' : '#FAFAFA', display: 'grid', gridTemplateColumns: '1fr 1fr',
      [`@media (max-width:${theme.breakpoints.sm}px)`]: { gridTemplateColumns: '1fr' },
    },
    contribCol: { padding: '16px 20px' },
    contribDivider: { borderRight: `1px solid ${dk ? '#333' : '#EEEEEE'}` },
    contribLabel: { fontSize: 11, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase' as const, color: dk ? '#909296' : '#AAAAAA', marginBottom: 10 },
    contribMetaLabel: { fontSize: 12, color: dk ? '#909296' : '#9CA3AF', marginBottom: 2 },
    contribValue: { fontSize: 16, fontWeight: 700, color: dk ? '#e0e0e0' : '#1A1A1A' },
    contribValueGreen: { color: dk ? 'rgb(34, 197, 94)' : 'rgb(34, 197, 94)', fontWeight: 700 },
    contribNote: { fontSize: 12, color: dk ? '#909296' : '#AAAAAA', marginTop: 6 },

    /* ── Section labels ── */
    secLabel: { fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase' as const, color: dk ? '#909296' : '#AAAAAA', margin: '22px 0 10px' },

    /* ── Tables ── */
    tWrap: { border: `1px solid ${dk ? '#333' : '#E8E8E8'}`, borderRadius: 10, overflow: 'hidden' },
    tRow: {
      display: 'grid',
      padding: '11px 16px',
      borderBottom: `1px solid ${dk ? '#2a2a2a' : '#F0F0F0'}`,
      alignItems: 'center',
      fontSize: 13,
      color: dk ? '#ccc' : '#1A1A1A',
    },
    /* Spec mobile rule: collapse the 6-column campaign / source tables to
       just Name + primary metric + Status on screens under sm. The grid
       template hides cells 2/3/5 entirely so there is no horizontal scroll. */
    tRow6: {
      gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
      [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
        gridTemplateColumns: '2fr 1fr 1fr',
        '& > *:nth-of-type(2), & > *:nth-of-type(3), & > *:nth-of-type(5)': {
          display: 'none',
        },
      },
    },
    tRowHeader: { background: dk ? '#2a2a2a' : '#F5F5F3', fontSize: 11, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase' as const, color: dk ? '#909296' : '#AAAAAA' },
    tRowBest: { background: dk ? '#142a1a' : '#F0FDF4' },
    tRowWorst: { background: dk ? '#2a1a14' : '#FEF3EE' },
    badge: { display: 'inline-block', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, letterSpacing: '.06em', textTransform: 'uppercase' as const, marginLeft: 7 },
    badgeBest: { background: dk ? 'rgb(34, 197, 94)' : '#BBFCBC', color: dk ? '#BBF7D0' : 'rgb(34, 197, 94)' },
    badgeReview: { background: dk ? 'rgb(245, 158, 11)' : '#FED7AA', color: dk ? '#FED7AA' : 'rgb(245, 158, 11)' },
    green: { color: 'rgb(34, 197, 94)', fontWeight: 600 },
    red: { color: 'rgb(239, 68, 68)', fontWeight: 600 },
    amber: { color: 'rgb(245, 158, 11)', fontWeight: 500 },
    dim: { color: dk ? '#909296' : '#AAAAAA', fontSize: 12 },

    /* ── Validation Note ── */
    valNote: { display: 'flex', gap: 8, background: dk ? '#1a2333' : '#EFF6FF', border: `1px solid ${dk ? '#2a3a55' : '#BFDBFE'}`, borderRadius: 8, padding: '11px 16px', marginTop: 12, fontSize: 12, color: dk ? '#93C5FD' : '#1E40AF', lineHeight: 1.5 },
    valLink: { color: dk ? '#60A5FA' : '#1D4ED8', fontWeight: 500, cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },

    /* ── Funnel ── */
    fWrap: {
      borderRadius: 10,
      padding: 16,
      backgroundColor: dk ? '#1A1B1E' : '#F5F5F5',
      overflowX: 'auto' as const,
      WebkitOverflowScrolling: 'touch' as const,
    },
    fRow: {
      display: 'flex',
      alignItems: 'stretch',
      gap: 8,
      width: 'max-content',
      minWidth: '100%',
    },
    fStep: {
      flex: 1,
      minWidth: 140,
      position: 'relative' as const,
      backgroundColor: dk ? '#25262B' : '#FFFFFF',
      border: `1px solid ${dk ? '#373A40' : '#E8E8E8'}`,
      borderRadius: 10,
      padding: '20px 16px 16px',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      textAlign: 'center' as const,
      overflow: 'hidden',
    },
    fStepConstraint: { border: '1px solid rgb(239, 68, 68)' },
    fAccentBar: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      backgroundColor: 'rgb(239, 68, 68)',
    },
    fArrow: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      paddingInline: 4,
      color: dk ? '#909296' : '#6B7280',
      flexShrink: 0,
    },
    fArrowDelta: { fontSize: 11, fontWeight: 700, color: 'rgb(239, 68, 68)' },
    fArrowGlyph: { fontSize: 18, lineHeight: 1 },
    fLabel: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      letterSpacing: 0.4,
      color: dk ? '#909296' : '#666',
      marginBottom: 8,
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
    },
    fN: {
      fontSize: 26,
      fontWeight: 700,
      lineHeight: 1.1,
      color: dk ? '#FFFFFF' : '#1A1A1A',
    },
    fName: {
      fontSize: 11,
      color: dk ? '#909296' : '#888',
      marginTop: 4,
    },
    fOppBadge: {
      marginTop: 10,
      display: 'inline-block',
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      letterSpacing: 0.5,
      color: 'rgb(239, 68, 68)',
      backgroundColor: 'transparent',
      border: '1px solid rgb(239, 68, 68)',
      borderRadius: 4,
      padding: '2px 8px',
    },
    fDrop: { fontSize: 11, fontWeight: 600, color: 'rgb(239, 68, 68)', marginTop: 2 },
    fOppBanner: { background: dk ? '#2A2415' : '#FFFBEB', border: `1px solid ${dk ? '#5c4718' : '#FDE68A'}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16 },
    fOppLabel: { fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' as const, color: 'rgb(245, 158, 11)', marginRight: 10, display: 'inline' },
    fOppText: { fontSize: 13, fontWeight: 700, display: 'inline', color: dk ? '#e0e0e0' : '#1A1A1A' },
    fInterpretation: { fontSize: 13, color: dk ? '#C1C2C5' : '#555', marginTop: 14 },
    fFooter: { marginTop: 16, paddingTop: 12, borderTop: `1px solid ${dk ? '#2a2a2a' : '#F0F0F0'}`, fontSize: 12, color: dk ? '#909296' : '#AAAAAA' },
    fFooterLink: { color: '#4F46E5', fontWeight: 500, cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },

    /* ── Tests ── */
    testsRow: { border: `1px solid ${dk ? '#333' : '#E8E8E8'}`, borderRadius: 10, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    testsLeft: { fontSize: 13, color: dk ? '#C1C2C5' : '#666' },
    testsRight: { fontSize: 12, color: '#4F46E5', fontWeight: 500, cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },

    /* ── Empty states ── */
    emptyHero: {
      borderRadius: 12,
      border: `1px solid ${dk ? '#333' : '#E8E8E8'}`,
      background: dk ? '#1E1E1E' : '#FAFAFA',
      padding: '28px 32px',
      display: 'flex',
      gap: 18,
      alignItems: 'center',
      flexWrap: 'wrap' as const,
    },
    emptyIcon: {
      width: 52, height: 52, borderRadius: '50%',
      background: dk ? '#2D2545' : '#EEF2FF',
      color: dk ? '#A78BFA' : '#4F46E5',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    },
    emptyTitle: { fontSize: 16, fontWeight: 700, color: dk ? '#FFFFFF' : '#1A1A1A', margin: 0 },
    emptyBody: { fontSize: 13, color: dk ? '#C1C2C5' : '#555', marginTop: 4, lineHeight: 1.5 },
    emptyMessage: {
      borderRadius: 10,
      border: `1px solid ${dk ? '#333' : '#E8E8E8'}`,
      background: dk ? '#1E1E1E' : '#FAFAFA',
      padding: '18px 22px',
      marginBottom: 14,
    },
    emptyMessageTitle: { fontSize: 14, fontWeight: 600, color: dk ? '#FFFFFF' : '#1A1A1A', margin: 0 },
    emptyMessageSub: { fontSize: 13, color: dk ? '#909296' : '#6B7280', marginTop: 6, lineHeight: 1.5 },

    /* ── Footer ── */
    dashFooter: { padding: '16px 0', borderTop: `1px solid ${dk ? '#2a2a2a' : '#F0F0F0'}`, fontSize: 12, color: dk ? '#909296' : '#AAAAAA', marginTop: 24, lineHeight: 1.6 },
    footerLink: { color: '#4F46E5', textDecoration: 'none', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } },
  };
});

/* ============================================================================
   EMPTY-STATE HELPERS
   ============================================================================ */

// Renders the empty state shown when the dashboard cannot fetch data.
// Behaviour (per spec):
//   • Site has campaigns → "No data available. Select campaigns in Settings."
//                           plus a tests container if tests are active.
//   • Site has no campaigns → skip the "Select campaigns" suggestion entirely.
//                              If tests exist, show a tests-only call-out.
//                              Otherwise fall back to a friendly empty state.
function renderDashboardEmptyState({
  classes,
  dk,
  siteHasCampaigns,
  activeTestsCount,
  onOpenSettings,
  onNavigateToTests,
}: {
  classes: any;
  dk: boolean;
  siteHasCampaigns: boolean;
  activeTestsCount: number;
  onOpenSettings?: () => void;
  onNavigateToTests: () => void;
}) {
  const hasTests = activeTestsCount > 0;
  const testsLine = hasTests
    ? `${activeTestsCount} active test${activeTestsCount === 1 ? '' : 's'} running`
    : 'No active tests running';

  // Reusable tests container (matches the in-dashboard testsRow style).
  const testsContainer = (
    <div className={classes.testsRow}>
      <span className={classes.testsLeft}>
        {hasTests ? <strong>{testsLine}</strong> : testsLine}
        {hasTests ? ' on this site' : ''}
      </span>
      <span className={classes.testsRight} onClick={onNavigateToTests}>
        View Test Results <IconArrowRight size={12} style={{ verticalAlign: -2 }} />
      </span>
    </div>
  );

  // Site has campaigns → original message + tests container below.
  if (siteHasCampaigns) {
    return (
      <>
        <div className={classes.emptyMessage}>
          <p className={classes.emptyMessageTitle}>No data available</p>
          <p className={classes.emptyMessageSub}>
            Select campaigns in Settings to populate this dashboard.
          </p>
          {onOpenSettings && (
            <Button
              size="xs"
              variant="light"
              leftIcon={<IconSettings size={14} />}
              mt={12}
              onClick={onOpenSettings}
            >
              Open Settings
            </Button>
          )}
        </div>
        {testsContainer}
      </>
    );
  }

  // Site has no campaigns → tests-only call-out when tests exist.
  if (hasTests) {
    return (
      <div className={classes.emptyHero}>
        <div className={classes.emptyIcon}>
          <IconFlask size={26} />
        </div>
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <p className={classes.emptyTitle}>{testsLine}</p>
          <p className={classes.emptyBody}>
            This site has active tests but no campaigns yet. Review the tests
            currently in flight to see what you're learning.
          </p>
        </div>
        <Button
          variant="filled"
          rightIcon={<IconArrowRight size={14} />}
          onClick={onNavigateToTests}
        >
          Review Tests
        </Button>
      </div>
    );
  }

  // Site has no campaigns and no active tests.
  return (
    <div className={classes.emptyHero}>
      <div className={classes.emptyIcon}>
        <IconFlask size={26} />
      </div>
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <p className={classes.emptyTitle}>Nothing running yet on this site</p>
        <p className={classes.emptyBody}>
          Once you launch a campaign or test, results will appear here.
        </p>
      </div>
    </div>
  );
}

/* ============================================================================
   MAIN COMPONENT
   ============================================================================ */

export const BusinessDashboardView: React.FC<BusinessDashboardViewProps> = ({
  data, costThreshold, dateRangeLabel, onNavigateToReports, onNavigateToTests, loading = false,
  siteHasCampaigns = true, activeTestsCount = 0, onOpenSettings,
}) => {
  const { classes, theme } = useStyles();
  const dk = theme.colorScheme === 'dark';

  // Skeleton scaffold while the dashboard payload is being fetched. Mirrors
  // the real layout (5 KPI cards, summary band, contributors, two tables) so
  // the page does not collapse and re-expand once data arrives.
  if (loading && (!data || !data.cards)) {
    return (
      <Box className={classes.body}>
        {/* 5 KPI Cards */}
        <div className={classes.kpiGrid}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className={classes.slot}>
              <Skeleton height={11} width="55%" radius="sm" mb={8} />
              <Skeleton height={11} width="40%" radius="sm" mb={14} />
              <Skeleton height={28} width="70%" radius="sm" mb={10} />
              <Skeleton height={11} width="60%" radius="sm" />
            </div>
          ))}
        </div>

        {/* Profitability summary */}
        <div style={{
          background: dk ? '#1e1e1e' : '#FAFAFA',
          border: `1px solid ${dk ? '#333' : '#E8E8E8'}`,
          borderRadius: 10,
          padding: '20px 24px',
          marginBottom: 16,
        }}>
          <Skeleton height={14} width={320} radius="sm" />
        </div>

        {/* Top Contributors */}
        <Skeleton height={11} width={160} radius="sm" mb={10} />
        <div className={classes.contribWrap}>
          <div className={`${classes.contribCol} ${classes.contribDivider}`}>
            <Skeleton height={11} width={90} radius="sm" mb={10} />
            <Skeleton height={11} width={140} radius="sm" mb={6} />
            <Skeleton height={16} width="80%" radius="sm" mb={14} />
            <Skeleton height={11} width={140} radius="sm" mb={6} />
            <Skeleton height={16} width="70%" radius="sm" />
          </div>
          <div className={classes.contribCol}>
            <Skeleton height={11} width={110} radius="sm" mb={10} />
            <Skeleton height={11} width={140} radius="sm" mb={6} />
            <Skeleton height={16} width="80%" radius="sm" mb={14} />
            <Skeleton height={11} width={140} radius="sm" mb={6} />
            <Skeleton height={16} width="70%" radius="sm" />
          </div>
        </div>

        {/* Performance Breakdown table */}
        <Skeleton height={11} width={200} radius="sm" mt={22} mb={10} />
        <div className={classes.tWrap}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className={`${classes.tRow} ${classes.tRow6}${i === 0 ? ` ${classes.tRowHeader}` : ''}`}
              style={i === 5 ? { borderBottom: 'none' } : undefined}
            >
              <Skeleton height={12} width="70%" radius="sm" />
              <Skeleton height={12} width="60%" radius="sm" style={{ marginLeft: 'auto' }} />
              <Skeleton height={12} width="50%" radius="sm" style={{ marginLeft: 'auto' }} />
              <Skeleton height={12} width="40%" radius="sm" style={{ marginLeft: 'auto' }} />
              <Skeleton height={12} width="55%" radius="sm" style={{ marginLeft: 'auto' }} />
              <Skeleton height={12} width="55%" radius="sm" style={{ marginLeft: 'auto' }} />
            </div>
          ))}
        </div>

        {/* Traffic Sources table */}
        <Skeleton height={11} width={240} radius="sm" mt={22} mb={10} />
        <div className={classes.tWrap}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className={`${classes.tRow} ${classes.tRow6}${i === 0 ? ` ${classes.tRowHeader}` : ''}`}
              style={i === 5 ? { borderBottom: 'none' } : undefined}
            >
              <Skeleton height={12} width="65%" radius="sm" />
              <Skeleton height={12} width="55%" radius="sm" style={{ marginLeft: 'auto' }} />
              <Skeleton height={12} width="45%" radius="sm" style={{ marginLeft: 'auto' }} />
              <Skeleton height={12} width="40%" radius="sm" style={{ marginLeft: 'auto' }} />
              <Skeleton height={12} width="50%" radius="sm" style={{ marginLeft: 'auto' }} />
              <Skeleton height={12} width="60%" radius="sm" style={{ marginLeft: 'auto' }} />
            </div>
          ))}
        </div>

        {/* Where your biggest opportunity is — funnel skeleton */}
        <Skeleton height={11} width={260} radius="sm" mt={22} mb={10} />
        <div className={classes.fWrap}>
          <div className={classes.fRow}>
            {Array.from({ length: 5 }).map((_, i) => (
              <React.Fragment key={i}>
                {i > 0 && (
                  <div className={classes.fArrow}>
                    <Skeleton height={11} width={28} radius="sm" />
                  </div>
                )}
                <div className={classes.fStep}>
                  <Skeleton height={11} width={70} radius="sm" mb={8} />
                  <Skeleton height={20} width={80} radius="sm" mb={8} />
                  <Skeleton height={10} width={60} radius="sm" />
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Tests row skeleton */}
        <Skeleton height={11} width={60} radius="sm" mt={22} mb={10} />
        <div className={classes.testsRow}>
          <Skeleton height={13} width={260} radius="sm" />
          <Skeleton height={13} width={120} radius="sm" />
        </div>
      </Box>
    );
  }

  if (!data || !data.cards) {
    return (
      <Box className={classes.body}>
        {renderDashboardEmptyState({
          classes,
          dk,
          siteHasCampaigns,
          activeTestsCount,
          onOpenSettings,
          onNavigateToTests,
        })}
      </Box>
    );
  }

  const c = data.cards;
  const d = data.deltas;
  const goalName = c.primary_goal_name || 'Primary Goal';
  const revName = c.revenue_event_name || '';
  const rows = data.campaign_rows || [];
  const srcRows = data.source_rows || [];
  const tc = data.top_contributors;
  const prof = data.profitability;
  const funnel = data.funnel;
  const tests = data.tests;
  const skippedCampaigns = data.warnings?.skipped_campaigns || [];

  // Spec: full empty state when the entire date range has no activity at all.
  // Show a polite "No data yet for this period." message instead of a row of zeros.
  const isFullyEmpty =
    (c.ad_spend == null || c.ad_spend === 0) &&
    (c.primary_goal_conversions == null || c.primary_goal_conversions === 0) &&
    rows.length === 0 &&
    srcRows.length === 0;

  const profColorMap: Record<string, string> = {
    strong_profitable: 'rgb(18, 184, 134)', low_profitable: 'rgb(18, 184, 134)', profitable: 'rgb(18, 184, 134)',
    break_even: 'rgb(245, 158, 11)', losing: 'rgb(239, 68, 68)', no_revenue: dk ? '#909296' : '#999',
  };
  const profBgMap: Record<string, string> = {
    strong_profitable: dk ? 'rgb(30, 30, 30)' : '#FAFAFA', low_profitable: dk ? 'rgb(30, 30, 30)' : '#FAFAFA', profitable: dk ? 'rgb(30, 30, 30)' : '#FAFAFA',
    break_even: dk ? 'rgb(30, 30, 30)' : '#FAFAFA', losing: dk ? 'rgb(30, 30, 30)' : '#FAFAFA', no_revenue: dk ? '#25262B' : '#FAFAFA',
  };
  const profBorderMap: Record<string, string> = {
    strong_profitable: 'rgb(18, 184, 134)', low_profitable: 'rgb(18, 184, 134)', profitable: 'rgb(18, 184, 134)',
    break_even: 'rgb(245, 158, 11)', losing: 'rgb(239, 68, 68)', no_revenue: dk ? '#373A40' : '#E8E8E8',
  };
  const profState = prof?.state || 'no_revenue';
  const profDotColor = profColorMap[profState] || '#999';
  const profBg = profBgMap[profState] || '#FAFAFA';
  const profBorder = profBorderMap[profState] || '#E8E8E8';
  const profitPerGoal = prof?.profit_per_goal;
  const cardProfitPerGoal = (c as any)?.profit_per_goal as number | null | undefined;

  // Best / worst campaign rows
  let bestIdx = -1, worstIdx = -1;
  if (rows.length >= 2) {
    rows.forEach((r, i) => { if (bestIdx < 0 || r.cost_per_goal < rows[bestIdx].cost_per_goal) bestIdx = i; });
    rows.forEach((r, i) => { if (i !== bestIdx && (worstIdx < 0 || r.cost_per_goal > rows[worstIdx].cost_per_goal)) worstIdx = i; });
  }

  return (
    <Box className={classes.body}>

      {/* Spec: dashboard-level empty state when no activity exists in the
          selected date range. Sits above the cards so the user sees it
          immediately without scrolling. */}
      {isFullyEmpty && (
        <div style={{
          background: dk ? '#25262B' : '#FAFAFA',
          border: `1px solid ${dk ? '#373A40' : '#E8E8E8'}`,
          borderRadius: 8,
          padding: '14px 16px',
          marginBottom: 14,
          fontSize: 13,
          color: dk ? '#909296' : '#6B7280',
          textAlign: 'center',
          lineHeight: 1.5,
        }}>
          No data yet for this period.
        </div>
      )}

      {/* Goal-mismatch warning: surfaces campaigns that were excluded from
          the totals because they don't contain the selected Primary Goal. */}
      {skippedCampaigns.length > 0 && (
        <div style={{
          background: dk ? '#2A2005' : '#FFFBEB',
          border: `1px solid ${dk ? '#5C4813' : '#FDE68A'}`,
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 14,
          fontSize: 13,
          color: dk ? 'rgb(245, 158, 11)' : 'rgb(245, 158, 11)',
          lineHeight: 1.5,
        }}>
          <strong>Some campaigns were excluded.</strong>{' '}
          The following campaigns don&apos;t contain the selected Primary Goal ({goalName}) and
          were not counted: {skippedCampaigns.join(', ')}. Open Settings to pick a goal that exists in every campaign.
        </div>
      )}

      {/* ── 1. Top 5 KPI Cards ── */}
      <div className={classes.kpiGrid}>
        {/* Card 1 — Ad Spend */}
        <div className={classes.slot}>
          <p className={classes.slotLabel}>Ad Spend</p>
          <p className={classes.slotGoal}>Total out this period</p>
          <p className={classes.slotVal}>{formatMoney(c.ad_spend)}</p>
          {d?.ad_spend != null && <p className={classes.slotSub}>{formatDeltaText(d.ad_spend, `vs prev ${dateRangeLabel}`)}</p>}
        </div>
        {/* Card 2 — Primary Goal (label per spec; sub-label is the exact goal name) */}
        <div className={classes.slot}>
          <p className={classes.slotLabel}>Primary Goal</p>
          <p className={classes.slotGoal}>{goalName}</p>
          <p className={classes.slotVal}>{formatCount(c.primary_goal_conversions)}</p>
          {d?.primary_goal_conversions != null && (
            <p className={classes.slotSub}>{formatDeltaText(d.primary_goal_conversions, `vs prev ${dateRangeLabel}`)}</p>
          )}
        </div>
        {/* Card 3 — Cost per [Primary Goal Name].
            Spec: 3-threshold colour only when a Campaign Report benchmark is loaded.
            green/amber/red derived from cost_per_goal_status (set by the backend
            using traffic_source_benchmarks for this goal). */}
        <div className={classes.slot}>
          <p className={classes.slotLabel}>Cost per {goalName}</p>
          <p className={classes.slotGoal}>
            {c.cost_per_goal_target != null && c.cost_per_goal_target > 0
              ? `Target: ${formatMoney(c.cost_per_goal_target)}`
              : '\u00A0'}
          </p>
          {c.cost_per_goal == null && (c.primary_goal_conversions ?? 0) === 0 ? (
            <Tooltip label={SPEND_EMPTY_TOOLTIP} withArrow>
              <p className={classes.slotVal}>—</p>
            </Tooltip>
          ) : (
            <p
              className={classes.slotVal}
              style={{ color: benchmarkColor(c.cost_per_goal_status) }}
            >
              {formatMoney(c.cost_per_goal)}
            </p>
          )}
          {d?.cost_per_goal != null && (
            <p className={classes.slotSub}>{formatDeltaText(d.cost_per_goal, `vs prev ${dateRangeLabel}`)}</p>
          )}
        </div>
        {/* Card 4 — Revenue per [Primary Goal Name] */}
        <div className={classes.slot}>
          <p className={classes.slotLabel}>Revenue per {goalName}</p>
          <p className={classes.slotGoal}>{revName ? `Based on ${revName}` : '\u00A0'}</p>
          {c.revenue_per_goal == null ? (
            <Tooltip label={REVENUE_EMPTY_TOOLTIP} withArrow>
              <p className={classes.slotVal}>—</p>
            </Tooltip>
          ) : (
            <p className={classes.slotVal}>{formatMoney(c.revenue_per_goal)}</p>
          )}
          {c.revenue_per_goal != null && d?.revenue_per_goal != null && (
            <p className={classes.slotSub}>{formatDeltaText(d.revenue_per_goal, `vs prev ${dateRangeLabel}`)}</p>
          )}
          {/* Inline "Profit / Loss per [Goal]" — derived metric beneath the
              % delta. Label flips to "Loss" when value is negative. Colour
              mirrors the profitability state used by the verdict box:
              losing → red, break_even → orange, profitable → green. */}
          {cardProfitPerGoal != null && (() => {
            const isLoss = cardProfitPerGoal < 0;
            const cardColor =
              profState === 'losing'
                ? 'rgb(239, 68, 68)'
                : profState === 'break_even'
                  ? 'rgb(245, 158, 11)'
                  : profState === 'low_profitable' || profState === 'strong_profitable'
                    ? 'rgb(34, 197, 94)'
                    : isLoss
                      ? 'rgb(239, 68, 68)'
                      : 'rgb(34, 197, 94)';
            return (
              <p
                className={classes.slotSub}
                style={{ marginTop: 2, fontWeight: 600, color: cardColor }}
              >
                {isLoss ? 'Loss' : 'Profit'} per {goalName}: {isLoss ? '−' : '+'}${Math.abs(cardProfitPerGoal).toFixed(2)}
              </p>
            );
          })()}
        </div>
        {/* Card 5 — Average Order Value */}
        <div className={classes.slot}>
          <p className={classes.slotLabel}>Average Order Value</p>
          <p className={classes.slotGoal}>{revName || '\u00A0'}</p>
          {c.avg_order_value == null ? (
            <Tooltip label={REVENUE_EMPTY_TOOLTIP} withArrow>
              <p className={classes.slotVal}>—</p>
            </Tooltip>
          ) : (
            <p className={classes.slotVal}>{formatMoney(c.avg_order_value)}</p>
          )}
        </div>
      </div>

      {/* ── 2. Profitability summary – margin-based verdict
          (losing / break_even / low_profitable / strong_profitable). ── */}
      <div style={{ background: profBg, border: `1px solid ${profBorder}`, borderRadius: 10, padding: '20px 24px', marginBottom: 16 }}>
        {profState === 'no_revenue' ? (
          <p style={{ fontSize: 13, color: dk ? '#FFFFFF' : '#888', margin: 0 }}>{prof?.title || 'Not enough data to determine performance'}</p>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span className={classes.summaryDot} style={{ background: profDotColor, flexShrink: 0 }} />
            {(() => {
              // Verdict is always black (or white in dark mode). Only the
              // profit/loss segment carries the state colour:
              //   profitable → green, losing → red, break-even → orange.
              const verdictColor = dk ? '#FFFFFF' : '#000000';
              let profitColor = dk ? '#FFFFFF' : '#000000';
              if (profState === 'break_even') {
                profitColor = 'rgb(245, 158, 11)';
              } else if (profitPerGoal != null && profitPerGoal >= 0) {
                profitColor = 'rgb(18, 184, 134)';
              } else if (profitPerGoal != null && profitPerGoal < 0) {
                profitColor = 'rgb(239, 68, 68)';
              }
              return (
                <>
                  {prof?.verdict && (
                    <span style={{ fontSize: 15, fontWeight: 600, color: verdictColor }}>
                      {prof.verdict}
                    </span>
                  )}
                  {profitPerGoal != null && (
                    <span style={{ fontSize: 15, fontWeight: 600, color: profitColor }}>
                      {profitPerGoal >= 0 ? 'Profit' : 'Loss'} per {goalName}: {profitPerGoal >= 0 ? '+' : '-'}${Math.abs(profitPerGoal).toFixed(2)}
                    </span>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* ── 3. Top Contributors ── */}
      {tc && (tc.best_campaign || tc.best_source) && (
        <>
          <p className={classes.secLabel}>What's Driving Results</p>
          <div className={classes.contribWrap}>
            <div className={`${classes.contribCol} ${classes.contribDivider}`}>
              <p className={classes.contribLabel}>Campaigns</p>
              <p className={classes.contribMetaLabel}>Top Campaign (by conversions)</p>
              <p className={classes.contribValue}>
                {tc.best_campaign?.campaign_name || '—'}
                {tc.best_campaign && (c.primary_goal_conversions ?? 0) > 0 && (
                  <span style={{ fontWeight: 500, color: '#6B7280', marginLeft: 6 }}>
                    ({Math.round(((tc.best_campaign.booked_calls || 0) * 100) / (c.primary_goal_conversions || 1))}% of total)
                  </span>
                )}
              </p>
              <p className={classes.contribMetaLabel} style={{ marginTop: 10 }}>Lowest Cost per Goal</p>
              <p className={classes.contribValue}>
                {tc.lowest_cp_goal_campaign?.campaign_name || '—'}
                {tc.lowest_cp_goal_campaign?.cost_per_goal != null && tc.lowest_cp_goal_campaign.cost_per_goal > 0 && (
                  <>
                    <span className={classes.contribValueGreen} style={{ marginLeft: 6 }}>{formatMoney(tc.lowest_cp_goal_campaign.cost_per_goal)}</span>
                    {tc.lowest_cp_goal_campaign.booked_calls != null && (
                      <span style={{ fontWeight: 500, color: '#6B7280', marginLeft: 6 }}>
                        ({formatCount(tc.lowest_cp_goal_campaign.booked_calls)} conversions)
                      </span>
                    )}
                  </>
                )}
              </p>
            </div>
            <div className={classes.contribCol}>
              <p className={classes.contribLabel}>Traffic Sources</p>
              <p className={classes.contribMetaLabel}>Top Source (by conversions)</p>
              <p className={classes.contribValue}>
                {tc.best_source?.source_name || '—'}
                {tc.best_source && (c.primary_goal_conversions ?? 0) > 0 && (
                  <span style={{ fontWeight: 500, color: '#6B7280', marginLeft: 6 }}>
                    ({Math.round(((tc.best_source.booked_calls || 0) * 100) / (c.primary_goal_conversions || 1))}% of total)
                  </span>
                )}
              </p>
              <p className={classes.contribMetaLabel} style={{ marginTop: 10 }}>Lowest Cost per Goal</p>
              <p className={classes.contribValue}>
                {tc.lowest_cp_goal_source?.source_name || '—'}
                {tc.lowest_cp_goal_source?.cost_per_goal != null && tc.lowest_cp_goal_source.cost_per_goal > 0
                  ? (
                    <>
                      <span className={classes.contribValueGreen} style={{ marginLeft: 6 }}>{formatMoney(tc.lowest_cp_goal_source.cost_per_goal)}</span>
                      {tc.lowest_cp_goal_source.booked_calls != null && (
                        <span style={{ fontWeight: 500, color: '#6B7280', marginLeft: 6 }}>
                          ({formatCount(tc.lowest_cp_goal_source.booked_calls)} conversions)
                        </span>
                      )}
                    </>
                  )
                  : <span style={{ fontWeight: 400, color: '#6B7280', marginLeft: 6 }}>No cost data available.</span>}
              </p>
            </div>
          </div>
          <p className={classes.contribNote}>Based on first visit</p>
        </>
      )}

      {/* ── 4. Performance Breakdown (campaigns) ──
          Capped at 5 rows per spec; remaining campaigns are reachable through
          the "View detailed reports →" deep-link to the Campaign Reports area. */}
      {rows.length > 0 && (
        <>
          <p className={classes.secLabel}>Performance Breakdown</p>
          <div className={classes.tWrap}>
            <div className={`${classes.tRow} ${classes.tRow6} ${classes.tRowHeader}`}>
              <div>Campaign</div><div style={{ textAlign: 'right' }}>Spend</div><div style={{ textAlign: 'right' }}>{goalName}</div><div style={{ textAlign: 'right' }}>% of Total</div><div style={{ textAlign: 'right' }}>Cost per {goalName}</div><div style={{ textAlign: 'right' }}>Revenue</div>
            </div>
            {rows.slice(0, 5).map((row, idx) => {
              const visibleCount = Math.min(rows.length, 5);
              const isBest = idx === bestIdx;
              const isWorst = idx === worstIdx;
              const pct = (c.primary_goal_conversions ?? 0) > 0 ? Math.round((row.booked_calls * 100) / (c.primary_goal_conversions || 1)) : 0;
              const costColor = benchmarkColor(row.cost_per_goal_status);
              return (
                <div key={idx} className={`${classes.tRow} ${classes.tRow6} ${isBest ? classes.tRowBest : isWorst ? classes.tRowWorst : ''}`} style={idx === visibleCount - 1 ? { borderBottom: 'none' } : undefined}>
                  <div>
                    {row.campaign_name}
                    {isBest && <span className={`${classes.badge} ${classes.badgeBest}`}>Best</span>}
                    {isWorst && <span className={`${classes.badge} ${classes.badgeReview}`}>Review</span>}
                  </div>
                  <div style={{ textAlign: 'right' }}>{formatMoney(row.spend)}</div>
                  <div style={{ textAlign: 'right' }}>{formatCount(row.booked_calls)}</div>
                  <div style={{ textAlign: 'right' }}>{pct}%</div>
                  <div style={{ textAlign: 'right', color: costColor, fontWeight: costColor ? 600 : undefined }}>
                    {row.cost_per_goal > 0 ? formatMoney(row.cost_per_goal) : '—'}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {row.revenue > 0 ? (
                      formatMoney(row.revenue)
                    ) : (
                      <Tooltip label={REVENUE_EMPTY_TOOLTIP} withArrow>
                        <span>—</span>
                      </Tooltip>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 6, marginBottom: 14, textAlign: 'right' }}>
            <span className={classes.fFooterLink} onClick={onNavigateToReports}>View detailed reports →</span>
          </div>
        </>
      )}

      {/* ── 5. Traffic Sources Table ──
          Capped at 5 rows per spec; deep-link below routes to Campaign Reports. */}
      {srcRows.length > 0 && (
        <>
          <p className={classes.secLabel}>Which traffic sources are working <span style={{ fontWeight: 400, textTransform: 'none' as const, letterSpacing: 0 }}>(based on first visit)</span></p>
          <div className={classes.tWrap}>
            <div className={`${classes.tRow} ${classes.tRow6} ${classes.tRowHeader}`}>
              <div>Source</div><div style={{ textAlign: 'right' }}>Unique Visitors</div><div style={{ textAlign: 'right' }}>{goalName}</div><div style={{ textAlign: 'right' }}>% of Total</div><div style={{ textAlign: 'right' }}>Cost per {goalName}</div><div style={{ textAlign: 'right' }}>Status</div>
            </div>
            {srcRows.slice(0, 5).map((row, idx) => {
              const visibleCount = Math.min(srcRows.length, 5);
              const pct = (c.primary_goal_conversions ?? 0) > 0 ? Math.round((row.booked_calls * 100) / (c.primary_goal_conversions || 1)) : 0;
              const hasCost = row.cost_per_goal > 0;
              // Spec: 3-threshold colours. Backend returns
              // 'green' | 'amber' | 'red' | 'no_benchmark' from traffic_source_benchmarks.
              const status = row.status as string | undefined;
              const dotMap: Record<string, string> = { green: 'rgb(34, 197, 94)', amber: 'rgb(245, 158, 11)', red: 'rgb(239, 68, 68)' };
              const labelMap: Record<string, string> = {
                green: 'Below target',
                amber: 'Near target',
                red: 'Above target',
              };
              const hasBenchmark = status === 'green' || status === 'amber' || status === 'red';
              const statusDotColor = hasBenchmark ? dotMap[status as string] : '#9CA3AF';
              const statusTextColor = hasBenchmark ? benchmarkColor(status) : '#6B7280';
              const statusLabel = !hasCost
                ? '—'
                : !hasBenchmark
                  ? 'No benchmark'
                  : labelMap[status as string];
              return (
                <div key={idx} className={`${classes.tRow} ${classes.tRow6}`} style={idx === visibleCount - 1 ? { borderBottom: 'none' } : undefined}>
                  <div>{row.source_name}</div>
                  <div style={{ textAlign: 'right' }}>{formatCount(row.unique_visitors)}</div>
                  <div style={{ textAlign: 'right' }}>{formatCount(row.booked_calls)}</div>
                  <div style={{ textAlign: 'right' }}>{pct}%</div>
                  <div style={{ textAlign: 'right', color: hasBenchmark ? statusTextColor : undefined, fontWeight: hasBenchmark ? 600 : undefined }}>
                    {hasCost ? formatMoney(row.cost_per_goal) : '—'}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {hasCost && (
                      <span style={{ width: 9, height: 9, borderRadius: 99, display: 'inline-block', marginRight: 8, background: statusDotColor }} />
                    )}
                    <span style={{ color: statusTextColor, fontSize: 12 }}>{statusLabel}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 6, marginBottom: 14, textAlign: 'right' }}>
            <span className={classes.fFooterLink} onClick={onNavigateToReports}>View detailed reports →</span>
          </div>
        </>
      )}

      {/* ── 6. Funnel: Where your biggest opportunity is ──
          White cards on a light grey background. All steps are neutral by
          default; the single step with the lowest progression rate gets an
          amber accent bar + "Biggest Opportunity" badge. */}
      {funnel?.steps && funnel.steps.length > 0 && (
        <>
          <p className={classes.secLabel}>Where your biggest opportunity is</p>
          <div className={classes.fWrap}>
            <div className={classes.fRow}>
              {(() => {
                // Pick the step with the lowest progression rate (skipping the entry step).
                let constraintIdx = -1;
                let lowestRate = Infinity;
                funnel.steps!.forEach((s, idx) => {
                  if (idx === 0) return;
                  const r = s.actual_rate;
                  if (r != null && r < lowestRate) {
                    lowestRate = r;
                    constraintIdx = idx;
                  }
                });

                return funnel.steps!.map((step, i) => {
                  const isFirst = i === 0;
                  const isConstraint = i === constraintIdx;

                  // Drop % between cards = 100 - progression%
                  const dropPct =
                    step.actual_rate != null
                      ? Math.max(0, 100 - step.actual_rate)
                      : null;
                  const dropLabel =
                    dropPct != null
                      ? `-${dropPct.toFixed(dropPct < 10 ? 1 : 0)}%`
                      : '';

                  const progressionInside =
                    !isFirst && step.actual_rate != null
                      ? `${step.actual_rate.toFixed(step.actual_rate < 10 ? 2 : 1)}% of previous step`
                      : '';

                  return (
                    <React.Fragment key={i}>
                      {i > 0 && (
                        <div className={classes.fArrow}>
                          {dropLabel && <span className={classes.fArrowDelta}>{dropLabel}</span>}
                          <span className={classes.fArrowGlyph}>{'›'}</span>
                        </div>
                      )}
                      <div className={`${classes.fStep} ${isConstraint ? classes.fStepConstraint : ''}`}>
                        {isConstraint && <div className={classes.fAccentBar} />}
                        <div className={classes.fLabel}>{step.label}</div>
                        <div className={classes.fN}>{(step.value || 0).toLocaleString('en-US')}</div>
                        <div className={classes.fName}>{isFirst ? 'Visitors' : 'Conversions'}</div>
                        {progressionInside && (
                          <div className={classes.fName}>{progressionInside}</div>
                        )}
                        {isConstraint && (
                          <div className={classes.fOppBadge}>Biggest Opportunity</div>
                        )}
                      </div>
                    </React.Fragment>
                  );
                });
              })()}
            </div>
          </div>
        </>
      )}

      {/* ── 7. View detailed reports link (outside funnel boundary, single path) ──
          Right-aligned for visual consistency with the per-table deep-links
          under Performance Breakdown and Traffic Sources. */}
      <div style={{ marginTop: 14, marginBottom: 14, textAlign: 'right' }}>
        <span className={classes.fFooterLink} onClick={onNavigateToReports}>View detailed reports →</span>
      </div>

      {/* ── 8. Tests ── */}
      <p className={classes.secLabel}>Tests</p>
      <div className={classes.testsRow}>
        <span className={classes.testsLeft}>
          {(tests?.active_tests_count ?? 0) > 0 ? (
            <>
              <strong>{tests?.active_tests_count} active test{(tests?.active_tests_count ?? 0) !== 1 ? 's' : ''}</strong>
              {' '}running across selected campaigns
            </>
          ) : (
            'No active tests running'
          )}
        </span>
        <span className={classes.testsRight} onClick={onNavigateToTests}>View Test Results →</span>
      </div>

      {/* ── 9. Footer — informational only, no links (spec: one path only, via #7 above) ── */}
      <div className={classes.dashFooter}>
        Data reflects {dateRangeLabel.toLowerCase()}. Review Campaign Report before making budget decisions.
      </div>
    </Box>
  );
};

export default BusinessDashboardView;
